/**
 * A Google place → the billing address fields the payment provider stores.
 *
 * Separate from the form because the mapping is the part that breaks quietly.
 * Two details it has to get right, both of which come from the column
 * definitions rather than from anything visible in the UI:
 *
 *   country  is a TWO-character column, so "Canada" cannot be stored — the ISO
 *            code is written, and the rows already on file hold "CA".
 *   state    is stored as the short code ("ON") by everything written so far,
 *            so the code wins when Google supplies one.
 *
 * `street` is empty for a business listing, where Google returns a name and a
 * formatted address but no street_number/route; the formatted address is the
 * only thing left to fall back on.
 */

// Everything the provider needs before it will accept the address.
export const REQUIRED_ADDRESS = ["address1", "city", "state", "postalCode", "country"];

const COUNTRY_LABEL = { CA: "Canada", US: "United States" };

export function billingAddressFromPlace(parsed) {
  const a = parsed || {};
  const fields = {
    address1: a.street || a.formatted_address || "",
    city: a.city || "",
    state: a.state_code || a.state || "",
    postalCode: a.postal_code || "",
    country: a.country_code || "",
  };
  return { fields, complete: REQUIRED_ADDRESS.every((k) => Boolean(fields[k])) };
}

/** The picked address written the way an address is written. */
export function addressLines(fields) {
  const f = fields || {};
  const country = COUNTRY_LABEL[f.country] || f.country || "";
  return [
    f.address1 || "",
    [f.city, f.state, f.postalCode].filter(Boolean).join(", "),
    country,
  ].filter(Boolean);
}


/**
 * The billing details on file, shaped for the card form, or null.
 *
 * A returning buyer already has a payment profile, so the modal skips the
 * billing-details step and the card form used to ask for the street and postal
 * code a second time. GET /payment/api/get-create-customer/ returns the row the
 * first purchase wrote; anything short of a clean answer with an address means
 * the form asks, as it always could.
 */
export function billingFromProfile(res) {
  // Two shapes reach here: the old GET /payment/api/get-create-customer/ body
  // (which carries its own `status`) and the `billing` object nested in
  // GET /usage/billing-profile/ (which does not, because its envelope already
  // answered 200). Requiring a status would silently drop the second and send a
  // returning buyer back to retyping their address.
  if (!res || res.error || !res.address1) return null;
  if (res.status !== undefined && res.status !== 200) return null;
  return {
    first_name: res.first_name || "",
    last_name: res.last_name || "",
    address1: res.address1 || "",
    city: res.city || "",
    state: res.state || "",
    postalCode: res.postalCode || "",
    country: res.country || "",
  };
}
